import { MeterReadingType } from '../api/meterReadingsApi';

/**
 * Match tiered usage fees (điện / nước đồng hồ) without hard-coded fee codes.
 * See backend usage-fee-category-matching.util.ts for the full strategy.
 */
export function normalizeUsageFeeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .trim();
}

export function isElectricVehicleFeeName(name: string): boolean {
  const normalized = normalizeUsageFeeText(name);
  if (normalized.includes('xe may dien')) return true;
  if (normalized.includes('xe dien')) return true;
  return false;
}

export function isChargingFeeCategory(category: {
  feeCode?: string | null;
  name?: string | null;
}): boolean {
  const code = (category.feeCode ?? '').trim().toUpperCase();
  if (code.startsWith('SAC')) return true;
  const normalized = normalizeUsageFeeText(category.name ?? '');
  return normalized.includes('phi sac');
}

export function isElectricVehicleUsageFee(category: {
  feeCode?: string | null;
  name?: string | null;
}): boolean {
  const name = category.name ?? '';
  if (isElectricVehicleFeeName(name)) return true;
  const code = (category.feeCode ?? '').trim().toUpperCase();
  return code.startsWith('XE') && normalizeUsageFeeText(name).includes('dien');
}

export function isWaterUsageFeeName(name: string): boolean {
  const normalized = normalizeUsageFeeText(name);
  if (normalized.includes('dien')) return false;
  return normalized.includes('nuoc');
}

export function isElectricityUsageFeeName(name: string): boolean {
  const normalized = normalizeUsageFeeText(name);
  if (isElectricVehicleFeeName(name)) return false;
  if (normalized.includes('nuoc')) return false;
  return normalized.includes('dien') || normalized.includes('ien');
}

export function isServiceDvFeeCode(feeCode?: string | null): boolean {
  return (feeCode ?? '').trim().toUpperCase().startsWith('DV');
}

export function isWaterUsageFeeCategory(category: {
  feeCode?: string | null;
  name?: string | null;
}): boolean {
  const name = category.name ?? '';
  if (isWaterUsageFeeName(name)) return true;
  const code = category.feeCode ?? '';
  return isServiceDvFeeCode(code) && isWaterUsageFeeName(name);
}

export function isElectricityUsageFeeCategory(category: {
  feeCode?: string | null;
  name?: string | null;
}): boolean {
  if (isChargingFeeCategory(category)) return false;
  if (isElectricVehicleUsageFee(category)) return false;
  const name = category.name ?? '';
  if (isElectricityUsageFeeName(name)) return true;
  const code = category.feeCode ?? '';
  return isServiceDvFeeCode(code) && isElectricityUsageFeeName(name);
}

export function isMeterTieredUsageFeeCategory(category: {
  feeCode?: string | null;
  name?: string | null;
}): boolean {
  return isWaterUsageFeeCategory(category) || isElectricityUsageFeeCategory(category);
}

export function matchesMeterReadingType(
  category: { feeCode?: string | null; name?: string | null },
  type: MeterReadingType,
): boolean {
  return type === MeterReadingType.WATER
    ? isWaterUsageFeeCategory(category)
    : isElectricityUsageFeeCategory(category);
}
